import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandS,
  BrandXXS,
  BrandM,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { glossary } from '@sorare/core/src/lib/glossary';
import { absCenter } from '@sorare/core/src/lib/style';
import { smallMobileHeightAndAbove } from '@sorare/core/src/style/mediaQuery';

import footballSealedCard from './assets/footballSealedCard.webp';
import mlbSealedCard from './assets/mlbSealedCard.webp';
import nbaSealedCard from './assets/nbaSealedCard.webp';

const mainMessage = defineMessages({
  title: {
    defaultMessage: 'Level Up',
  },
  subtitle: {
    defaultMessage:
      'Sealing a card increases your Vault & Player Loyalty Levels',
  },
  footballSubtitle: {
    defaultMessage:
      'Sealing a card increases your Vault Level. You can Unseal 1 card every 14 days per scarcity.',
  },
});

const cardImageBySport = {
  [Sport.BASEBALL]: mlbSealedCard,
  [Sport.NBA]: nbaSealedCard,
  [Sport.FOOTBALL]: footballSealedCard,
};

const subtitleBySport = {
  [Sport.BASEBALL]: mainMessage.subtitle,
  [Sport.NBA]: mainMessage.subtitle,
  [Sport.FOOTBALL]: mainMessage.footballSubtitle,
};

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  flex: 1;
  isolation: isolate;
  justify-content: space-between;

  & > * {
    z-index: 1;
    position: relative;
    isolation: isolate;
  }
`;

const Content = styled(Vertical)`
  width: 100%;
  flex: 1;
`;

const Padded = styled(Horizontal)`
  padding: 0 var(--double-unit);
  flex: 1;
  display: flex;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const TextContent = styled(Vertical)`
  height: 80px;
  display: flex;
  align-items: center;
  position: relative;

  @media ${smallMobileHeightAndAbove} {
    margin-top: var(--triple-unit);
  }

  & > * {
    padding-inline: var(--double-unit);
  }
`;

const Img = styled(animated.img)`
  margin: 0 auto;
  max-width: 240px;
  pointer-events: none;
  user-select: none;
`;

const SpaceAroundVertical = styled(Vertical)`
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const BarContainer = styled(Vertical)`
  width: 100%;
  max-width: 300px;
  margin-top: var(--unit);
`;

const BarGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  position: relative;
`;

const Score = styled(BrandXXS)`
  ${absCenter}
`;

type Props = {
  nextStep: () => void;
  prevStep: () => void;
};

export const LevelUp = ({ nextStep, prevStep }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const [direction, setDirection] = useState<'next' | 'back'>('next');

  const contentSpring = useSpring({
    opacity: 1,
    transform: 'translateX(0%)',
    from: {
      opacity: 0,
      transform:
        direction === 'next' ? 'translateX(100%)' : 'translateX(-100%)',
    },
    config: { tension: 200, friction: 20 },
  });

  const imageSpring = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: { tension: 200, friction: 20 },
  });

  if (!currentUser) return null;

  return (
    <Wrapper>
      <Content gap={3}>
        <TextContent gap={0.5}>
          <animated.div
            style={{ ...contentSpring, position: 'absolute', width: '100%' }}
          >
            <BrandM className="text-center">
              <FormattedMessage
                {...mainMessage.title}
                values={{ nickname: currentUser?.nickname }}
              />
            </BrandM>
          </animated.div>
        </TextContent>
        <SpaceAroundVertical center>
          <Img
            style={imageSpring}
            src={cardImageBySport[sport]}
            alt=""
            className="boxImage"
          />
          <BarContainer gap={2}>
            <BarContainer gap={1}>
              <Horizontal>
                <BrandS>
                  <FormattedMessage
                    defaultMessage="{rarity} vault"
                    values={{ rarity: formatScarcity('limited') }}
                  />
                </BrandS>
                <BrandS
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color':
                        'var(--c-vault-limited-progressbar)',
                    } as CSSProperties
                  }
                >
                  LVL 2
                </BrandS>
              </Horizontal>
              <BarGrid>
                <SplitProgressBar
                  thresholds={[{ score: 2.5 }, { score: 10 }]}
                  currentScore={2.5}
                  color="var(--c-vault-limited-progressbar)"
                  variant="oblique"
                  split={false}
                />
                <Score>25/100</Score>
              </BarGrid>
            </BarContainer>
          </BarContainer>
        </SpaceAroundVertical>
        <Padded center>
          <Box>
            <LabelL color="var(--c-nd-600)" className="text-center">
              <FormattedMessage {...subtitleBySport[sport]} />
            </LabelL>
          </Box>
        </Padded>
      </Content>
      <ButtonWrapper>
        <StyledButton
          fullWidth
          size="large"
          color="quaternary"
          disableDebounce
          onClick={() => {
            setDirection('back');
            prevStep();
          }}
        >
          <FormattedMessage {...glossary.back} />
        </StyledButton>
        <StyledButton
          fullWidth
          size="large"
          color="secondary"
          disableDebounce
          onClick={() => {
            setDirection('next');
            nextStep();
          }}
        >
          <FormattedMessage {...glossary.next} />
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
